import { FormGroup } from '@angular/forms';
import { isEqual } from 'lodash-es';
import { FormGroupRules } from '@shared/models/interfaces/form-group-rules.model';
import { addRemoveError, checkBoxHelper } from '@shared/helpers/form.helper';
import { isNilOrEmpty } from '@shared/helpers/utils.helper';
import { toNum } from '@shared/helpers/math.helper';
import { INCONGRUENT_NUMBER, INCONSISTENT_CHOICE, REQUIRED_STATUS } from '@shared/constants/shared.constants';
import { formPaths } from '@shared/constants/form-paths.const';
import { getCoopEdilizieControl } from './helpers/coop-edilizie-form.helpers';

const FON_FIN_PATHS = ['my_FonFinContiScop', 'my_FonFinMutuiOrd', 'my_FonFinMutuiAgev', 'my_FonFinSoci', 'my_FonFinAltro'];

export const schedaCoopEdilizieRules: FormGroupRules = {
    my_CkIscrizAlbo: (form: FormGroup) => {
        requiredByScEdilObbl(form, 'my_CkIscrizAlbo');
    },
    my_CkEnteConsorzio: (form: FormGroup) => {
        requiredByScEdilObbl(form, 'my_CkEnteConsorzio');
    },
    my_CkPropDivisa: (form: FormGroup) => {
        requiredByScEdilObbl(form, 'my_CkPropDivisa');
    },
    my_numPrenotat: (form: FormGroup) => {
        requiredByScEdilObbl(form, 'my_numPrenotat');
    },
    my_CkOssPrenotAlloggi: (form: FormGroup) => {
        requiredByScEdilObbl(form, 'my_CkOssPrenotAlloggi');
    },
    my_CkSociNonPrenot: (form: FormGroup) => {
        requiredByScEdilObbl(form, 'my_CkSociNonPrenot');
    },
    my_CkCdASociPrenOAss: (form: FormGroup) => {
        requiredByScEdilObbl(form, 'my_CkCdASociPrenOAss');
    },
    my_DescCdASociPrenOAss: (form: FormGroup) => {
        requiredByScEdilObbl(form, 'my_DescCdASociPrenOAss');
    },
    my_SchEdFinanzPubb: (form: FormGroup) => {
        requiredByScEdilObbl(form, 'my_SchEdFinanzPubb');
    },

    my_CkDelibModifStat: (form: FormGroup) => {
        requiredWhenNewCoop(form, 'my_CkDelibModifStat');
    },
    my_CkPresentRelaz: (form: FormGroup) => {
        requiredWhenNewCoop(form, 'my_CkPresentRelaz');
    },
    my_CkPrescrittaQuota: (form: FormGroup) => {
        requiredWhenNewCoop(form, 'my_CkPrescrittaQuota');
    },
    my_CkSociSotto18: (form: FormGroup) => {
        requiredWhenNewCoop(form, 'my_CkSociSotto18');
    },
    my_CkCoopCons: (form: FormGroup) => {
        requiredWhenNewCoop(form, 'my_CkCoopCons');
    },
    my_CkMantenutaIscrSez: (form: FormGroup) => {
        requiredWhenNewCoop(form, 'my_CkMantenutaIscrSez');
    },

    my_CkSociSubentratiGiu: (form: FormGroup) => {
        requiredWhenNewCoopAndNotCons(form, 'my_CkSociSubentratiGiu');
    },

    my_CkSociSubentratiCoop: (form: FormGroup) => {
        const ckSociSubentratiGiu = getCoopEdilizieControl(form, 'my_CkSociSubentratiGiu').value;
        requiredWhenNewCoopAndNotCons(form, 'my_CkSociSubentratiCoop', ckSociSubentratiGiu !== '0');
    },

    my_NaturaEnteCoop: (form: FormGroup) => {
        const ckSociSubentratiCoop = getCoopEdilizieControl(form, 'my_CkSociSubentratiCoop').value;
        const ckSociSubentratiGiu = getCoopEdilizieControl(form, 'my_CkSociSubentratiGiu').value;
        requiredWhenNewCoopAndNotCons(form, 'my_NaturaEnteCoop', ckSociSubentratiCoop === '0' && ckSociSubentratiGiu !== '0');
    },

    my_CkSociSubentratiFis: (form: FormGroup) => {
        requiredWhenNewCoopAndCons(form, 'my_CkSociSubentratiFis');
    },

    my_CkGiuSubentratiCoop: (form: FormGroup) => {
        requiredWhenNewCoopAndCons(form, 'my_CkGiuSubentratiCoop');
    },

    my_NaturaEnteSubCons: (form: FormGroup) => {
        const ckGiuSubentratiCoop = getCoopEdilizieControl(form, 'my_CkGiuSubentratiCoop').value;
        requiredWhenNewCoopAndCons(form, 'my_NaturaEnteSubCons', ckGiuSubentratiCoop === '0');
    },

    my_CkCopiaStatDep: (form: FormGroup) => {
        ckHelper(form, 'my_CkCopiaStatDep', 'my_CkDelibModifStat', 1);
    },

    my_SpecConsorzio: (form: FormGroup) => {
        ckHelper(form, 'my_SpecConsorzio', 'my_CkEnteConsorzio', 1);
    },

    my_descOssPrenotAlloggi: (form: FormGroup) => {
        ckHelper(form, 'my_descOssPrenotAlloggi', 'my_CkOssPrenotAlloggi', 1);
    },

    my_ruoloSociNonPrenot: (form: FormGroup) => {
        ckHelper(form, 'my_ruoloSociNonPrenot', 'my_CkSociNonPrenot', 1);
    },

    my_CkCommissSociPrenot: (form: FormGroup) => {
        ckHelper(form, 'my_CkCommissSociPrenot', 'my_CkInCorsoProgrammaRealizzazione', 1);
    },

    my_numAlloggiInCorsoRealiz: (form: FormGroup) => {
        ckHelper(form, 'my_numAlloggiInCorsoRealiz', 'my_CkInCorsoProgrammaRealizzazione', 1);
    },

    my_DataRelazAnnuale: (form: FormGroup) => {
        relazAnnualeHelper(form, 'my_DataRelazAnnuale');
    },

    my_DataRelazAnnualeAnnoRif: (form: FormGroup) => {
        relazAnnualeHelper(form, 'my_DataRelazAnnualeAnnoRif');
    },

    my_FonFin: (form: FormGroup) => {
        fonFinHelper(form);
    },

    my_FonFinAltroDesc: (form: FormGroup) => {
        const control = getCoopEdilizieControl(form, 'my_FonFinAltroDesc');
        const fonFinAltro: string = getCoopEdilizieControl(form, 'my_FonFinAltro').value;
        const scEdilObbl: string = getCoopEdilizieControl(form, 'my_ScEdilObbl').value;
        const errors = control.errors ?? {};
        addRemoveError(isNilOrEmpty(control.value) && toNum(fonFinAltro) === 1 && scEdilObbl === '1', errors, 'my_FonFinAltroDesc', { status: REQUIRED_STATUS });
        control.setErrors(isEqual(errors, {}) ? null : errors);
    },

    my_NumPosiz: (form: FormGroup) => {
        const control = getCoopEdilizieControl(form, 'my_NumPosiz');
        const iscrizAlbo: string = getCoopEdilizieControl(form, 'my_CkIscrizAlbo').value;
        const scEdilObbl: string = getCoopEdilizieControl(form, 'my_ScEdilObbl').value;
        const errors = control.errors ?? {};
        addRemoveError(isNilOrEmpty(control.value) && (iscrizAlbo === '2' || iscrizAlbo === '3') && scEdilObbl === '1', errors, 'my_NumPosiz', { status: REQUIRED_STATUS });
        control.setErrors(isEqual(errors, {}) ? null : errors);
    },

    my_SpecPropDivisa: (form: FormGroup) => {
        const control = getCoopEdilizieControl(form, 'my_SpecPropDivisa');
        const ckPropDivisa: string = getCoopEdilizieControl(form, 'my_CkPropDivisa').value;
        const scEdilObbl: string = getCoopEdilizieControl(form, 'my_ScEdilObbl').value;
        const errors = control.errors ?? {};
        addRemoveError(isNilOrEmpty(control.value) && ckPropDivisa === '4' && scEdilObbl === '1', errors, 'my_SpecPropDivisa', { status: REQUIRED_STATUS });
        control.setErrors(isEqual(errors, {}) ? null : errors);
    },

    my_CkAvviatoProgrammaRealizzazione: (form: FormGroup) => {
        const control = getCoopEdilizieControl(form, 'my_CkAvviatoProgrammaRealizzazione');
        const numProgEdRealizz: string = getCoopEdilizieControl(form, 'my_numProgEdRealizz').value;
        const scEdilObbl: string = getCoopEdilizieControl(form, 'my_ScEdilObbl').value;
        const errors = control.errors ?? {};
        addRemoveError(isNilOrEmpty(control.value) && scEdilObbl === '1', errors, 'my_CkAvviatoProgrammaRealizzazioneRequired', { status: REQUIRED_STATUS });
        addRemoveError(control.value === '0' && toNum(numProgEdRealizz) > 0 && scEdilObbl === '1', errors, 'my_CkAvviatoProgrammaRealizzazioneIncoerente', {
            status: INCONSISTENT_CHOICE,
            message: "Incoerente: sono stati indicati programmi di realizzazione ma la risposta è 'No'",
        });
        control.setErrors(isEqual(errors, {}) ? null : errors);
    },

    my_numProgEdRealizz: (form: FormGroup) => {
        const control = getCoopEdilizieControl(form, 'my_numProgEdRealizz');
        const ckAvviato: string = getCoopEdilizieControl(form, 'my_CkAvviatoProgrammaRealizzazione').value;
        const scEdilObbl: string = getCoopEdilizieControl(form, 'my_ScEdilObbl').value;
        const errors = control.errors ?? {};
        addRemoveError(isNilOrEmpty(control.value) && scEdilObbl === '1', errors, 'my_numProgEdRealizzRequired', { status: REQUIRED_STATUS });
        addRemoveError(!isNilOrEmpty(control.value) && toNum(control.value) <= 0 && ckAvviato === '1' && scEdilObbl === '1', errors, 'my_numProgEdRealizz', {
            status: INCONGRUENT_NUMBER,
            message: "Incoerente con la risposta fornita alla domanda 'La cooperativa ha avviato un programma di realizzazione?'",
        });
        control.setErrors(isEqual(errors, {}) ? null : errors);
    },

    my_CkInCorsoProgrammaRealizzazione: (form: FormGroup) => {
        const control = getCoopEdilizieControl(form, 'my_CkInCorsoProgrammaRealizzazione');
        const ckAvviato: string = getCoopEdilizieControl(form, 'my_CkAvviatoProgrammaRealizzazione').value;
        const scEdilObbl: string = getCoopEdilizieControl(form, 'my_ScEdilObbl').value;
        const errors = control.errors ?? {};
        addRemoveError(isNilOrEmpty(control.value) && scEdilObbl === '1', errors, 'my_CkInCorsoProgrammaRealizzazione', { status: REQUIRED_STATUS });
        addRemoveError(control.value === '1' && ckAvviato === '0' && scEdilObbl === '1', errors, 'my_CkInCorsoProgrammaRealizzazione', {
            status: INCONSISTENT_CHOICE,
            message: "Incoerente con la risposta fornita alla domanda 'La cooperativa ha avviato un programma di realizzazione?'",
        });
        control.setErrors(isEqual(errors, {}) ? null : errors);
    },

    my_CkContiBancSing: (form: FormGroup) => {
        const control = getCoopEdilizieControl(form, 'my_CkContiBancSing');
        const numAlloggi: string = getCoopEdilizieControl(form, 'my_numAlloggiInCorsoRealiz').value;
        const scEdilObbl: string = getCoopEdilizieControl(form, 'my_ScEdilObbl').value;
        const errors = control.errors ?? {};
        addRemoveError(isNilOrEmpty(control.value) && toNum(numAlloggi) > 1 && scEdilObbl === '1', errors, 'my_CkContiBancSing', { status: REQUIRED_STATUS });
        control.setErrors(isEqual(errors, {}) ? null : errors);
    },
};

const requiredByScEdilObbl = (form: FormGroup, targetPath: string): void => {
    checkBoxHelper(form, targetPath, 'my_ScEdilObbl', 1, formPaths.schedaCoopEdil);
};

const requiredWhenNewCoop = (form: FormGroup, targetPath: string, suppCondition = true): void => {
    const control = getCoopEdilizieControl(form, targetPath);
    const iscrizAlbo: string = getCoopEdilizieControl(form, 'my_CkIscrizAlbo').value;
    const scEdilObbl: string = getCoopEdilizieControl(form, 'my_ScEdilObbl').value;
    const errors = control.errors ?? {};
    addRemoveError(isNilOrEmpty(control.value) && iscrizAlbo !== '1' && scEdilObbl === '1' && suppCondition, errors, targetPath, { status: REQUIRED_STATUS });
    control.setErrors(isEqual(errors, {}) ? null : errors);
};

const requiredWhenNewCoopAndNotCons = (form: FormGroup, targetPath: string, suppCondition = true): void => {
    const ckCoopCons: string = getCoopEdilizieControl(form, 'my_CkCoopCons').value;
    requiredWhenNewCoop(form, targetPath, ckCoopCons === '0' && suppCondition);
};

const requiredWhenNewCoopAndCons = (form: FormGroup, targetPath: string, suppCondition = true): void => {
    const ckCoopCons: string = getCoopEdilizieControl(form, 'my_CkCoopCons').value;
    requiredWhenNewCoop(form, targetPath, ckCoopCons === '1' && suppCondition);
};

const ckHelper = (form: FormGroup, targetPath: string, ckPath: string, ckTargetValue: number, suppCondition = true, errorStatus?: string): void => {
    const scEdilObbl: string = getCoopEdilizieControl(form, 'my_ScEdilObbl').value;
    checkBoxHelper(form, targetPath, ckPath, ckTargetValue, formPaths.schedaCoopEdil, suppCondition && scEdilObbl === '1', errorStatus);
};

const relazAnnualeHelper = (form: FormGroup, targetPath: string): void => {
    const control = getCoopEdilizieControl(form, targetPath);
    const iscrizAlbo: string = getCoopEdilizieControl(form, 'my_CkIscrizAlbo').value;
    const ckPresentRelaz: string = getCoopEdilizieControl(form, 'my_CkPresentRelaz').value;
    const scEdilObbl: string = getCoopEdilizieControl(form, 'my_ScEdilObbl').value;
    const errors = control.errors ?? {};
    addRemoveError(isNilOrEmpty(control.value) && iscrizAlbo !== '1' && ckPresentRelaz === '1' && scEdilObbl === '1', errors, targetPath, { status: REQUIRED_STATUS });
    control.setErrors(isEqual(errors, {}) ? null : errors);
};

const fonFinHelper = (form: FormGroup): void => {
    const scEdilObbl: string = getCoopEdilizieControl(form, 'my_ScEdilObbl').value;
    const allEmpty = FON_FIN_PATHS.every((path) => {
        const value = getCoopEdilizieControl(form, path).value;
        return isNilOrEmpty(value);
    });
    FON_FIN_PATHS.forEach((path) => {
        const control = getCoopEdilizieControl(form, path);
        const errors = control.errors ?? {};
        addRemoveError(allEmpty && scEdilObbl === '1', errors, 'my_FonFinRequired', {
            status: REQUIRED_STATUS,
            message: 'Indicare almeno una opzione',
        });
        control.setErrors(isEqual(errors, {}) ? null : errors);
    });
};

export const CoopEdilizieFormValidators = Object.values(schedaCoopEdilizieRules);
